/**
 * AppFrame comes with an OIDC based authentication / authorization layer.
 * Auth Services can be used to dynamically access some important authentication
 * related information like the access token, user object, user profile etc.
 *
 * @module Services > Feature Services > Auth Services
 */

import { LogLevel, UnsubscribeMethod } from 'types/common/base';

import { User, UserManager, UserManagerSettings, UserProfile } from '../../external';

/**
 * The authentication mode of AppFrame
 * There are three authentication modes, which depend on the oidc config of the main configuration.
 */
export enum AuthMode {
  /**
   * When `oidc` property is not set in the main configuration, the AppFrame is used in `NoLogin` mode.
   * In 'NoLogin' mode the AppFrame can be used anonymously only. There is no way to sign in the user
   * and no OIDC client is created.
   */
  NoLogin = 'NoLogin',
  /**
   * When `oidc` prop is set in the main configuration, but `oidc.allowAutoLogin: false` is set in the
   * main configuration, the AppFrame is configured to run in 'Anonymous' mode. In this case the user is not signed
   * in automatically, but a 'Login' button is displayed in the header. The user can sign in by clicking the button
   * and is redirected to the OIDC provider for authentication.
   */
  Anonymous = 'Anonymous',
  /**
   * When `oidc` prop is set in the main configuration and `oidc.allowAutoLogin` is not set or it is set to `true`,
   * the AppFrame is configured to run in 'LoggedIn' mode. In this case the user is must sign in automatically
   * when the AppFrame is loaded. The user is redirected to the OIDC provider for authentication.
   */
  LoggedIn = 'LoggedIn',
}

/**
 * An interface representing the rights of the user.
 *
 * Its keys are mapped to the `AppFrameRight` string union to make it extendable
 * with module augmentation. So the type definition of the `AppFrameRights` interface
 * can be extended with:
 * ```ts
 * declare module 'types/services/appframe/auth' {
 *  interface AppFrameRights {
 *    OpenDocument;
 *    SaveDocument;
 *   }
 * }
 * ```
 * Which will create a new type `AppFrameRight` with the following string union:
 * ```ts
 * type AppFrameRight = 'OpenDocument' | 'SaveDocument';
 * ```
 */
export interface AuthRights {}

/**
 * A string union representing the rights of the user.
 */
export type AuthRight = keyof AuthRights;

/**
 * An interface representing the audiences set for the client.
 *
 * Its keys are mapped to the `AppFrameRight` string union to make it extendable
 * with module augmentation. So the type definition of the `AppFrameRights` interface
 * can be extended with:
 * ```ts
 * declare module 'types/services/appframe/auth' {
 *  interface AppFrameAudiences {
 *    MyApp1;
 *    MyApp2;
 *   }
 * }
 * ```
 * Which will create a new type `AppFrameRight` with the following string union:
 * ```ts
 * type AppFrameAudience = 'MyApp1' | 'MyApp2';
 * ```
 */
export interface AuthAudiences {}

/**
 * A string union representing the audiences of the client.
 */
export type AuthAudience = keyof AuthAudiences;

/**
 * An option to mock the actual user from oidc
 */
export interface AuthMockUserProfile {
  /**
   * The first name of the user mapped by role mappers
   */
  firstName?: string;

  /**
   * The last name of the user mapped by role mappers
   */
  lastName?: string;

  /**
   * The roles belongs to the user mapped by role mappers.
   */
  roles?: string | string[];

  /**
   * The tenant identifier of the currently logged user mapped by role mappers.
   */
  tenantId?: string;

  /**
   * Contains the username which will be displayed in the app header.
   */
  preferred_username: string;

  /**
   * Describes the user's actual rights.
   */
  rights: string[];
}

/**
 * Basic configuration object for OIDC client manager
 */
export interface AuthManagerBaseConfig {
  /**
   * The client id from original `UserManagerSettings` object which is used to create the OIDC client.
   */
  client_id?: string;
  /**
   * Same as `client_id` in OIDC client.
   *
   * When both `client_id` and `clientId` is set the `clientId` will be used.
   */
  clientId: string;
  /**
   * Set it to `false` to set AppFrame into `Anonymous` authentication mode. This means that AppFrame
   * won't sign in the user automatically, but a "Login" button is displayed in the header.
   *
   * AppFrame login can be triggered anytime with `services.appFrame.auth.login()` method.
   *
   * @default true
   */
  allowAutoLogin?: boolean;
  /**
   * Mock user object which can be used to test the application without OIDC authentication.
   *
   * It is applied to a user object when the following requiremets are met:
   * - `oidc.mockUser` is set in the main configuration
   * OR
   * - in the main configuration: `oidc` property is set to `null` and `mockUser` object is set (deprecated)
   *
   * The `mockUser` object is used to simulate the OIDC user object.
   * This is useful for development purposes only.
   */
  mockUser?: AuthMockUserProfile;
  /**
   * The log level of the OIDC client.
   */
  logLevel?: LogLevel;
}

/**
 * This interface is extending the `OidcClientSettings` interface of `oidc-client-ts` library.
 */
export interface AuthManagerConfig extends AuthManagerBaseConfig, Omit<UserManagerSettings, 'client_id'> {}

export interface AuthManagerConstructor {
  new (config: AuthManagerConfig, moduleId?: string | null, online?: boolean);
}

/**
 * The interface of the `AuthManager` class which is the OIDC client manager.
 *
 * AppFrame can have multiple instance of `AuthManager` class. Each instance is
 * representing an OIDC client. The root instance is the main OIDC client that
 * is used for authentication and authorization. It can be configured under the
 * `oidc` property in the main configuration.
 * All other instances are considered module-specific OIDC clients. They can be
 * configured under the `oidc` property of the module configuration.
 */
export interface AuthManagerInterface {
  /**
   * Retrieves the actual user object.
   *
   * @returns The OIDC user object or `null` if the user is not authenticated.
   */
  getUser: () => User | null;
  /**
   * Subscribe to user changes.
   *
   * @param callback The callback method which is invoked when the user is changed.
   * @returns A function to unsubscribe from the user change event.
   */
  onUserChange: (callback: (user: User | null) => void) => UnsubscribeMethod;
  /**
   * Retrieves the actual user profile object.
   *
   * @returns The OIDC user profile object or `null` if the user is not authenticated.
   */
  getProfile: () => UserProfile | null;
  /**
   * Retrieves the actual access token.
   *
   * @returns The access token string.
   *          If the user is not authenticated or the access token is expired or not available, it returns `null`.
   *          If the user is authenticated and the access token is available, it returns the access token string.
   */
  getAccessToken: () => string | null;
  /**
   * Retrieves the actual access token but automatically refreshes it if it's expired.
   *
   * @returns The access token string when token is active or a promise which returns
   *          the token after silent token renewal (silent login).
   */
  getAccessTokenAsync: () => string | null | Promise<string | null>;
  /**
   * Retrieves the actual rights of the user.
   *
   * The rights object are decoded from the `rights` property of the `userinfo` object
   * provided by the OIDC provider.
   *
   * The rights are mapped to the `AppFrameRight` string union to make it extendable
   * with module augmentation. So the type definition of the `AppFrameRights` interface
   * can be extended with:
   * ```ts
   * declare module '@lsy-netline/appframe/types/services/appframe/auth' {
   *   interface AppFrameRights {
   *     OpenDocument;
   *     SaveDocument;
   *   }
   * }
   * ```
   *
   * @returns The rights array or an empty array if the user is not authenticated.
   */
  getRights: () => AuthRight[];
  /**
   * Retrieves the actual rights of the user as a map object.
   *
   * @returns The rights map object where the keys are the rights and the values are
   *         the same as the keys.
   */
  getRightsMap: () => Record<AuthRight, true>;
  /**
   * Retrieves the actual audiences of the user.
   *
   * The audiences are decoded from the `aud` property of the access token.
   *
   * The audiences are mapped to the `AppFrameAudience` string union to make it extendable
   * with module augmentation. So the type definition of the `AppFrameAudiences` interface
   * can be extended with:
   * ```ts
   * declare module '@lsy-netline/appframe/types/services/appframe/auth' {
   *   interface AppFrameAudiences {
   *    MyApp1;
   *    MyApp2;
   *   }
   * }
   * ```
   *
   * @returns The audiences array or an empty array if the user is not authenticated.
   */
  getAudiences: () => AuthAudience[];
  /**
   * Retrieves the actual audiences of the user as a map object.
   *
   * @returns The audiences map object where the keys are the audiences and the values are
   *         the same as the keys.
   */
  getAudiencesMap: () => Record<AuthAudience, true>;
  /**
   * Retrieves the online / offline status of the manager.
   *
   * Auth manager can be initialized in online or offline mode when the constructor is
   * called:
   * - In online mode the auth manager will try to connect to the OIDC provider
   * and will try to get the user object.
   * - In offline mode the auth manager will not try to connect to the OIDC provider
   * and will try to get the saved user object from the local storage.
   *
   * @returns `true` if the manager is initiliazed in online mode, `false` otherwise.
   */
  isOnline: () => boolean;
  /**
   * Initializes the auth manager.
   *
   * @returns A promise which resolves without any value when the user is authenticated.
   */
  initialize: () => Promise<void>;
  /**
   * Performs a login with the OIDC provider.
   *
   * It tries to get the user object from the OIDC provider once the OIDC session exists.
   * It behaves differently on root and module specific OIDC clients:
   * - On root OIDC client it will redirect the user to the OIDC provider for authentication.
   * - On module specific OIDC client it will try to get the user object from the OIDC provider
   *  silently or via a popup window.
   *
   * The login will be performed also silently if the user is authenticated and the `onlySilent` parameter
   * is set to `true`. In this case the method returns `null` when the user could not be authenticated
   * (eg. it is not logged on or the OIDC session is expired).
   *
   * @param onlySilent If `true` the login will be performed only silently.
   * @returns The OIDC user object or `null` if the user could not be authenticated.
   */
  login: (onlySilent?: boolean) => Promise<User | null>;
  /**
   * Performs a logout with the OIDC provider.
   *
   * It removes the OIDC session and the user object from the session storage.
   *
   * @param silentLogout If `true` the logout will be performed silently.
   * @returns A promise which resolves without any value when the logout is completed.
   */
  destroy: (silentLogout?: boolean) => Promise<void> | void;
  /**
   * Retrieves the underlying `UserManager` object of `oidc-client-ts` library.
   *
   * @returns The OIDC `UserManager` object if exists othwise `null`
   */
  getUserManager: () => UserManager | null;
}

/**
 * Returns the actual access token.
 *
 * @param moduleId The module id if the access token of a module specific OIDC is required.
 *
 * @returns The access token string.
 */
export type GetAccessTokenService = (moduleId?: string | null) => string | null;

/**
 * Returns the actual access token but automatically refreshes it if it's expired.
 *
 * @param moduleId The module id if the access token of a module specific OIDC is required.
 *
 * @returns The access token string when token is active or a promise which returns
 *          the token after silent token renewal (silent login).
 */
export type GetAccessTokenAsyncService = (moduleId?: string | null) => string | null | Promise<string | null>;

/**
 * Returns the ODIC user object.
 *
 * @param moduleId The module id if the user of a module specific OIDC is required.
 *
 * @returns The OIDC user object or null if the user is not authenticated.
 */
export type GetUserService = (moduleId?: string | null) => User | null;

/**
 * Returns the OIDC user profile object.
 *
 * @param moduleId The module id if the user profile of a module specific OIDC is required.
 */
export type GetProfileService = (moduleId?: string | null) => UserProfile | null;

/**
 * Initiates a redirect (root) sign in procedure.
 *
 */
export type LoginService = () => void;

/**
 * Signs out the application from every OIDC session.
 *
 * After the signout the AppFrame will be redirected to the login page
 *
 * Note: The logout message is not supported since AppFrame v1.47.4.
 */
export type LogoutService = (message?: string) => Promise<void> | void;

/**
 * Logout the user silently from every OIDC session without any user interaction.
 *
 * It is the caller responsibility how the application will behave once the logout
 * happened (promise resolved).
 *
 * NOTE: it is an experimental feature. Please use it cautiously!
 *
 * @experimental
 */
export type SilentLogoutService = () => Promise<void> | void;

/**
 * Returns the logout message which will be displayed on the logout page.
 *
 * @deprecated Since AppFrame v1.47.4 logout message is not supported
 */
export type GetLogoutMessageService = () => string | null;

/**
 * Sets the session expired flag to the given value.
 *
 * @param sessionIsExpired The new session expired flag.
 */
export type SetSessionExpiredService = (sessionIsExpired: boolean) => void;

/**
 * Returns the session expired flag.
 */
export type IsSessionExpiredService = () => boolean;

/**
 * Callback method which is invoked when the session is expired.
 *
 * @param sessionIsExpired The new session expired flag.
 */
export type OnSessionExpiredServiceCallback = (sessionIsExpired: boolean) => void;

/**
 * Subscribe to session expired changes.
 *
 * @param callback Callback method which is invoked when the session is expired.
 */
export type OnSessionExpiredService = (callback: OnSessionExpiredServiceCallback) => void;

/**
 * Callback method which is invoked when the user is changed.
 */
export type OnUserChangeServiceCallback = (user: User | null) => void;

/**
 * Subscribe to user changes.
 *
 * @param callback The callback method which is invoked when the user is changed.
 * @param moduleId The module id if the access token of a module specific OIDC is required.
 */
export type OnUserChangeService = (callback: OnUserChangeServiceCallback) => UnsubscribeMethod;

/**
 * Returns true if the sign-in is allowed for the given module.
 */
export type GetAuthModeService = () => AuthMode;

/**
 * Initializes the root or a module authorization manager (OIDC client instance)
 *
 * Note: It is used internally by AppFrame, so it is not recommended to use it directly.
 */
export type CreateAuthManagerService = (
  config?: AuthManagerConfig | null,
  moduleId?: string | null,
) => AuthManagerInterface | null;

/**
 * Destroys the root or a module authorization manager (OIDC client instance).
 *
 * While the OIDC client interface is not yet initialized and the remove
 * client method is called, the client creation will be stopped as soon as
 * possible but the client will be removed suddenly.
 *
 * If the auth manager is a root manager, it will also be logged out from the OIDC provider.
 *
 * Note: It is used internally by AppFrame, so it is not recommended to use it directly.
 */
export type DestroyAuthManagerService = (moduleId?: string | null, silentLogout?: boolean) => void;

/**
 * Retrieves the root or a module authorization manager.
 */
export type GetAuthManagerService = (moduleId?: string | null) => AuthManagerInterface | null;

/**
 * Services to manage user authentication and authorization.
 */
export interface AuthServices {
  /**
   * Returns the actual access token.
   */
  getAccessToken: GetAccessTokenService;
  /**
   * Returns the actual access token but automatically refreshes it if it's expired.
   */
  getAccessTokenAsync: GetAccessTokenAsyncService;
  /**
   * Returns the OIDC user object.
   */
  getUser: GetUserService;
  /**
   * Subscribe to user changes.
   */
  onUserChange: OnUserChangeService;
  /**
   * Returns the OIDC user profile object.
   */
  getProfile: GetProfileService;
  /**
   * Initiates a redirect (root) sign in procedure.
   */
  login: LoginService;
  /**
   * Signs out the application from every OIDC clients.
   */
  logout: LogoutService;
  /**
   * Logout the user silently from every OIDC clients without any user interaction.
   */
  silentLogout: SilentLogoutService;
  /**
   * Returns the logout message which will be displayed on the logout page.
   * @deprecated Logout service is not supported since AppFrame v1.47.4.
   */
  getLogoutMessage: GetLogoutMessageService;
  /**
   * Sets the session expired flag to the given value.
   */
  setSessionExpired: SetSessionExpiredService;
  /**
   * Returns the session expired flag.
   */
  isSessionExpired: IsSessionExpiredService;
  /**
   * Subscribe to session expired changes.
   */
  onSessionExpired: OnSessionExpiredService;
  /**
   * Returns true if the sign-in is allowed for the given module.
   */
  getAuthMode: GetAuthModeService;
  /**
   * Initializes the root or a module authorization manager (OIDC client instance)
   */
  createAuthManager: CreateAuthManagerService;
  /**
   * Destroys the root or a module authorization manager (OIDC client instance).
   */
  destroyAuthManager: DestroyAuthManagerService;
  /**
   * Retrieves the root or a module authorization manager.
   */
  getAuthManager: GetAuthManagerService;
  /**
   * Implement a local storage for the current user
   */
  userStorage: Storage;
}

declare module 'types/services/appframe' {
  interface AppFrameServices {
    auth: AuthServices;
  }
}
